import { useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { PencilSimple, Plus, ArrowCounterClockwise, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CATEGORY_ICON, intervalText } from "./complianceUtils";

export function ComplianceTypes({ types, categories, canEdit, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [adding, setAdding] = useState(false);
  const grouped = categories.map((c) => ({ c, items: types.filter((t) => t.category === c) })).filter((g) => g.items.length);

  const save = async (t) => {
    try {
      await API.put(`/compliance/types/${t.key}`, { interval_months: Number(draft.interval_months), lead_days: Number(draft.lead_days) });
      toast.success(`${t.short} updated`);
      setEditing(null); onChanged?.();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't update"); }
  };
  const reset = async (t) => {
    try { await API.delete(`/compliance/types/${t.key}`); toast.success(t.custom ? "Custom type removed" : "Reset to library default"); onChanged?.(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't remove"); }
  };

  return (
    <div className="space-y-4" data-testid="compliance-types">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Renewal interval and lead time (how far ahead a renewal job is raised) for every statutory requirement. Edits apply to all properties using that type.</p>
        {canEdit && <Button size="sm" onClick={() => setAdding(true)} data-testid="compliance-type-add"><Plus size={14} className="mr-1" /> Custom type</Button>}
      </div>
      {grouped.map(({ c, items }) => {
        const I = CATEGORY_ICON[c] || CATEGORY_ICON.Other;
        return (
          <div key={c} className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 border-b border-border text-xs font-semibold uppercase tracking-wider"><I size={14} /> {c}</div>
            <table className="w-full text-sm">
              <tbody>
                {items.map((t) => (
                  <tr key={t.key} className="border-b border-border last:border-0" data-testid={`compliance-type-${t.key}`}>
                    <td className="px-3 py-2">
                      <p className="font-medium">{t.name} {t.custom && <span className="ml-1 rounded bg-primary/10 px-1 text-[10px] text-primary">custom</span>}{t.overridden && <span className="ml-1 rounded bg-amber-500/15 px-1 text-[10px] text-amber-700">edited</span>}</p>
                      <p className="text-[11px] text-muted-foreground">{[t.legislation, t.who].filter(Boolean).join(" · ")}</p>
                    </td>
                    {editing === t.key ? (
                      <>
                        <td className="px-2 py-2 w-28"><Input type="number" min="1" value={draft.interval_months} onChange={(e) => setDraft((d) => ({ ...d, interval_months: e.target.value }))} className="h-8" data-testid="type-edit-interval" /><span className="text-[10px] text-muted-foreground">months</span></td>
                        <td className="px-2 py-2 w-28"><Input type="number" min="0" value={draft.lead_days} onChange={(e) => setDraft((d) => ({ ...d, lead_days: e.target.value }))} className="h-8" data-testid="type-edit-lead" /><span className="text-[10px] text-muted-foreground">days lead</span></td>
                        <td className="px-2 py-2 w-24 text-right whitespace-nowrap">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => save(t)} data-testid="type-edit-save"><Check size={14} /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(null)}><X size={14} /></Button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-2 py-2 w-28 text-xs"><span className="font-semibold">{intervalText(t.interval_months)}</span><span className="text-muted-foreground"> interval</span></td>
                        <td className="px-2 py-2 w-28 text-xs"><span className="font-semibold">{t.lead_days}d</span><span className="text-muted-foreground"> lead</span></td>
                        <td className="px-2 py-2 w-24 text-right whitespace-nowrap">
                          {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(t.key); setDraft({ interval_months: t.interval_months, lead_days: t.lead_days }); }} data-testid={`type-edit-${t.key}`}><PencilSimple size={14} /></Button>}
                          {canEdit && (t.custom || t.overridden) && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => reset(t)} title={t.custom ? "Remove custom type" : "Reset to library default"} data-testid={`type-reset-${t.key}`}><ArrowCounterClockwise size={14} /></Button>}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
      <AddTypeDialog open={adding} onOpenChange={setAdding} categories={categories} onSaved={() => { setAdding(false); onChanged?.(); }} />
    </div>
  );
}

function AddTypeDialog({ open, onOpenChange, categories, onSaved }) {
  const [f, setF] = useState({ name: "", short: "", category: "Other", interval_months: 12, lead_days: 30, legislation: "", who: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await API.post("/compliance/types", f); toast.success("Custom type added"); onSaved(); setF({ name: "", short: "", category: "Other", interval_months: 12, lead_days: 30, legislation: "", who: "" }); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't add"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="compliance-type-dialog">
        <DialogHeader><DialogTitle>Custom compliance type</DialogTitle><DialogDescription>Anything statutory or contractual you track per building that isn't in the library.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required className="mt-1" placeholder="e.g. Shutter door service" data-testid="type-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Short label</Label><Input value={f.short} onChange={(e) => setF({ ...f, short: e.target.value })} className="mt-1" placeholder="Shutters" data-testid="type-short" /></div>
            <div><Label>Category</Label><Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger className="mt-1" data-testid="type-category"><SelectValue /></SelectTrigger><SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Interval (months)</Label><Input type="number" min="1" value={f.interval_months} onChange={(e) => setF({ ...f, interval_months: e.target.value })} className="mt-1" data-testid="type-interval" /></div>
            <div><Label>Lead time (days)</Label><Input type="number" min="0" value={f.lead_days} onChange={(e) => setF({ ...f, lead_days: e.target.value })} className="mt-1" data-testid="type-lead" /></div>
          </div>
          <div><Label>Legislation / standard</Label><Input value={f.legislation} onChange={(e) => setF({ ...f, legislation: e.target.value })} className="mt-1" placeholder="Optional" /></div>
          <div><Label>Who may carry it out</Label><Input value={f.who} onChange={(e) => setF({ ...f, who: e.target.value })} className="mt-1" placeholder="Competent person…" /></div>
          <DialogFooter><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button type="submit" disabled={busy} data-testid="type-submit">{busy ? "Saving…" : "Add type"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
